import React, { useEffect, useRef, useState } from 'react';
import {
  Container,
  Stack,
  Backdrop,
  CircularProgress,
  Typography,
  TextField,
  Button
} from '@mui/material';
import { Form, FormikProvider, useFormik } from 'formik';
import { LoadingButton } from '@mui/lab';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import UploadPreview from '../components/uploads/UploadPreview';
import Page from '../components/Page';
import { getWork, newWork, updateWork } from '../utils/requests/workRequest';

export default function AddVideoProject() {
  const [isLoading, setIsLoading] = useState(false);
  const [video, setVideo] = useState('');

  const navigate = useNavigate();

  const [titleError, setTitleError] = useState('');
  const [companyError, setCompanyError] = useState('');

  const { id } = useParams();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const titleInput = useRef();

  /**
   * @type {React.MutableRefObject<HTMLInputElement>}
   */
  const companyInput = useRef();

  useEffect(() => {
    if (!id) return;
    getWork(id).then((work) => {
      titleInput.current.value = work.title;
      companyInput.current.value = work.details;
      const video = document.getElementById('video');
      video.src = work.gallery.url;
      video.play();
    });
  }, [id]);

  const formik = useFormik({
    initialValues: {},
    onSubmit: () => {
      const title = titleInput.current?.value;
      const company = companyInput.current?.value;

      setTitleError(false);
      setCompanyError(false);

      setIsLoading(true);

      let work;
      if (id) {
        work = updateWork(id, title, company, video, 2);
      } else {
        work = newWork(title, company, video, 2);
      }

      work
        .then((response) => {
          toast.success(response.data.message);
          navigate('../../');
        })
        .catch((error) => {
          if (error.response) {
            console.log(error.response);
            const { data } = error.response;
            if (data.data.title) {
              setTitleError(data.data.title[0]);
            }
            if (data.data.details) {
              setCompanyError(data.data.details[0]);
            }
            if (data.data.file) {
              toast.error(data.data.file[0]);
            }
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  });

  /**
   * @param {React.ChangeEvent<HTMLInputElement>} e
   */
  function onFileChange(e) {
    const media = URL.createObjectURL(e.target.files[0]);
    const video = document.getElementById('video');
    video.src = media;
    video.play();
    setVideo(e.target.files[0]);
  }

  return (
    <Page>
      <Container>
        <Stack spacing={2}>
          <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={isLoading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>
        </Stack>
        <Typography variant="h4" sx={{ mb: 5 }}>
          Add new video project
        </Typography>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate>
            <Stack style={{ width: '100%' }} spacing={2}>
              <UploadPreview
                onChange={(e) => onFileChange(e)}
                style={{ width: '100%' }}
                accept="video/*"
              />
              <video id="video" controls style={{ width: '50%' }}>
                <track kind="captions" />
              </video>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                  error={titleError}
                  disabled={isLoading}
                  helperText={titleError}
                  fullWidth
                  type="text"
                  label="Project title"
                  inputRef={titleInput}
                />
                <TextField
                  error={companyError}
                  helperText={companyError}
                  fullWidth
                  disabled={isLoading}
                  type="text"
                  label="Company"
                  inputRef={companyInput}
                />
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <LoadingButton loading={isLoading} variant="contained" type="submit">
                  Save
                </LoadingButton>
                <Button disabled={isLoading} variant="text" onClick={() => navigate('../../')}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Form>
        </FormikProvider>
      </Container>
    </Page>
  );
}
